import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import { AppService } from '@services/app.service';
import { ToastrService } from 'ngx-toastr';
import { NgxSpinnerService } from 'ngx-spinner';
import { finalize, catchError, of } from 'rxjs';
import { PricingPackage, PackageStatus, PricingFeature } from '@pages/modal/pricing-plans/pricingPlans';

declare var bootstrap: any;

@Component({
  selector: 'app-pricing-plans',
  templateUrl: './pricing-plans.component.html',
  styleUrls: ['./pricing-plans.component.scss']
})
export class PricingPlansComponent implements OnInit {

  pricingPlans: PricingPackage[] = [];
  addPackageForm!: FormGroup;
  addModal: any;
  PackageStatus = PackageStatus;
  submitted = false;

  headerColors = ['#A0522D', '#4682B4', '#556B2F', '#CC9900', '#6A5ACD'];
  cardColors = ['#FBF6F4', '#F5F9FC', '#F7FAF6', '#FFFBF6', '#F7F5FC'];

  constructor(
    private fb: FormBuilder,
    private appService: AppService,
    private toastr: ToastrService,
    private spinner: NgxSpinnerService
  ) {}

  ngOnInit(): void {
    this.loadPlans();
    this.initForm();
  }

  loadPlans() {
    this.spinner.show();
    this.appService.getAllPackages()
      .pipe(
        finalize(() => this.spinner.hide()),
        catchError(err => {
          console.error('Error loading pricing plans:', err);
          this.toastr.error('Failed to load pricing plans', 'Error');
          return of([]);
        })
      )
      .subscribe((res: PricingPackage[]) => {
        this.pricingPlans = res;
        if (res.length === 0) this.toastr.info('No pricing plans found', 'Info');
      });
  }

  initForm() {
    this.addPackageForm = this.fb.group({
      packageId: [null],
      packageName: ['', Validators.required],
      description: [''],
      priceMonthly: ['', Validators.required],
      priceYearly: ['', Validators.required],
      isMonthly: [true], // default ON
      isYearly: [true],  // default ON
      packageStatus: [PackageStatus.ACTIVE, Validators.required],
      pricingFeature: this.fb.array([this.createFeatureGroup()])
    });
  }

  createFeatureGroup(feature?: PricingFeature): FormGroup {
    return this.fb.group({
      featureId: [feature?.featureId || null],
      featureName: [feature?.featureName || '', Validators.required],
      hasOption: [feature?.value === 'yes' ? 'yes' : feature?.value === 'no' ? 'no' : ''],
      extraValue: [feature && feature.value !== 'yes' && feature.value !== 'no' ? feature.value : '']
    });
  }

  get features(): FormArray {
    return this.addPackageForm.get('pricingFeature') as FormArray;
  }

  addFeatureField() {
    this.features.push(this.createFeatureGroup());
  }

  removeFeatureField(index: number) {
    const featureControl = this.features.at(index);
    const featureId = featureControl.get('featureId')?.value;

    if (featureId) {
      this.spinner.show();
      this.appService.deleteFeatureById(featureId)
        .pipe(finalize(() => this.spinner.hide()))
        .subscribe({
          next: () => {
            this.toastr.success('Feature deleted successfully', 'Success');
            this.features.removeAt(index);
            this.ensureAtLeastOneFeature();
          },
          error: err => {
            console.error('Failed to delete feature:', err);
            this.toastr.error('Failed to delete feature', 'Error');
          }
        });
    } else {
      this.features.removeAt(index);
      this.ensureAtLeastOneFeature();
    }
  }

  ensureAtLeastOneFeature() {
    if (this.features.length === 0) this.addFeatureField();
  }

  openAddModal(pkg?: PricingPackage) {
    const modalEl = document.getElementById('addPackageModal');
    this.addModal = new bootstrap.Modal(modalEl);
    this.features.clear();

    if (pkg) {
      this.addPackageForm.patchValue({
        packageId: pkg.packageId,
        packageName: pkg.packageName,
        description: pkg.description,
        priceMonthly: pkg.priceMonthly,
        priceYearly: pkg.priceYearly,
        isMonthly: pkg.isMonthly ?? true,
        isYearly: pkg.isYearly ?? true,
        packageStatus: pkg.packageStatus
      });

      if (pkg.featureDetails?.length) {
        pkg.featureDetails.forEach(f => this.features.push(this.createFeatureGroup(f)));
      } else this.addFeatureField();
    } else {
      this.addPackageForm.reset({ packageStatus: PackageStatus.ACTIVE, isMonthly: true, isYearly: true });
      this.addFeatureField();
    }

    this.submitted = false;
    this.addModal.show();
  }

  setFeatureValue(index: number, value: string) {
    const control = this.features.at(index);
    if (value === 'yes' || value === 'no') {
      control.patchValue({ hasOption: value, extraValue: '' });
    } else {
      control.patchValue({ hasOption: '', extraValue: value });
    }
  }

  featureValid(feature: FormGroup): boolean {
    const hasOption = feature.get('hasOption')?.value;
    const extraValue = feature.get('extraValue')?.value;
    return !!(hasOption || (extraValue && extraValue.trim() !== ''));
  }

  savePackage() {
    this.submitted = true;

    if (this.addPackageForm.invalid) {
      this.toastr.warning('Please fill all required fields', 'Warning');
      return;
    }

    const allValid = this.features.controls.every(f => this.featureValid(f as FormGroup));
    if (!allValid) {
      this.toastr.warning('Please fill all feature values', 'Warning');
      return;
    }

    const formData = this.addPackageForm.value;
    const features: PricingFeature[] = (formData.pricingFeature || [])
      .filter(f => f.featureName && f.featureName.trim() !== '')
      .map(f => ({
        featureId: f.featureId || null,
        featureName: f.featureName.trim(),
        value: f.hasOption === 'yes' ? 'yes'
             : f.hasOption === 'no' ? 'no'
             : f.extraValue?.trim() || ''
      }));

    const packageData: PricingPackage = {
      packageId: formData.packageId,
      packageName: formData.packageName,
      description: formData.description,
      priceMonthly: formData.isMonthly ? formData.priceMonthly : null,
      priceYearly: formData.isYearly ? formData.priceYearly : null,
      isMonthly: formData.isMonthly,
      isYearly: formData.isYearly,
      packageStatus: formData.packageStatus,
      featureDetails: features
    };

    this.spinner.show();

    const apiCall = packageData.packageId
      ? this.appService.updatePackage(packageData)
      : this.appService.savePackage(packageData);

    apiCall.pipe(finalize(() => this.spinner.hide()))
      .subscribe({
        next: () => {
          this.toastr.success(`Package ${packageData.packageId ? 'updated' : 'added'} successfully`, 'Success');
          this.loadPlans();
          this.addModal.hide();
          this.submitted = false;
        },
        error: err => {
          console.error('Failed to save/update package:', err);
          this.toastr.error('Failed to save/update package', 'Error');
        }
      });
  }

  deletePackage(pkg: PricingPackage) {
    if (!pkg.packageId || !confirm('Are you sure you want to delete this package?')) return;

    this.spinner.show();
    this.appService.deletePackageById(pkg.packageId)
      .pipe(finalize(() => this.spinner.hide()))
      .subscribe({
        next: () => {
          this.loadPlans();
          this.toastr.success('Package deleted successfully', 'Success');
        },
        error: err => {
          console.error('Failed to delete package:', err);
          this.toastr.error('Failed to delete package', 'Error');
        }
      });
  }

}
